// Fill the Monthly Calculator from real sources, distributed per month.
//
//   • Payments      ← Ejar vouchers        (Contract/GetContractVouchers)
//   • Salik/Parking/Other ← Ejar other charges (Contract/GetContractOtherCharges)
//   • Fines         ← the fines database    (fines engine, via finesIndex)
//
// Each item lands in the month row whose window contains its date, exactly the way
// the user works the sheet by hand: "شهر 1 تحط اوزر شارج شهر 1 وبايمنت شهر 1".
// Ejar lumps Salik + Parking + genuine Other into one "Other Charges" bucket, so we
// split by the charge comment (chosen with the user: smart split into the 3 columns).
//
// Rent / VAT / dates / invoice no. / KM / uploads are left untouched — only the
// operational columns (salik, parking, fine, other, paid) and their detail lists are
// (re)filled, so this is a re-runnable sync, not a one-shot import.

import { parseSheetDate, parseRevenueDate } from './dates';
import { withVat } from '../../../constants/finance';
import { voucherSign, isDepositVoucher } from '../../../services/ejarVouchers';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Robust date parse for the ISO / "YYYY-MM-DD HH:mm:ss" stamps Ejar and the engine use. */
const toDate = (s) => {
    if (!s) return null;
    if (s instanceof Date) return isNaN(s) ? null : s;
    const viaRevenue = parseRevenueDate(s);
    if (viaRevenue && !isNaN(viaRevenue)) return viaRevenue;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
};

/**
 * Classify an Ejar "Other Charges" row by its comment into a calculator column. ``isParking`` is the engine's own
 * mark when the row carries one (a staff "Marina Walk-DXB 29.07.2026" is parking - owner, 13 Oct 2026); without it
 * the comment's words decide. The engine twin: fines_pipeline/monthly_fill.classify_other_charge.
 */
export const classifyOtherCharge = (comment, isParking) => {
    const c = String(comment || '').toLowerCase();
    if (c.includes('salik')) return 'salik';
    if (typeof isParking === 'boolean') return isParking ? 'parking' : 'other';
    if (c.includes('parkonic') || c.includes('parking')) return 'parking';
    return 'other';
};

/**
 * Salik trips on one Ejar Salik charge line (CON-16: the Excel's 'Salik Count' is counted, not guessed from
 * the amount). The engine's grouped line "Salik till 20/09/2026 (BB 10214_12, CC 5_3)" carries each car's
 * trips after the '_' (15 here); any other Salik line is one trip ("Salik Al Barsha -> Jebel Ali 05/09/2026 ...").
 */
export const salikTripsOf = (comment) => {
    // "Salik [& Darb] till ..." and, since 3 Oct 2026, "Salik [& Darb] from ... till ..." - monthly_fill.py twin
    const m = /salik\s*(?:&\s*darb\s*)?(?:from\s[^(]*?)?till[^(]*\(([^)]*)\)/i.exec(String(comment || ''));
    if (m) {
        const counts = (m[1].match(/_\d+/g) || []).map((x) => parseInt(x.slice(1), 10));
        if (counts.length) return counts.reduce((a, b) => a + b, 0);
    }
    return 1;
};

/**
 * The Salik period one Ejar Salik line names: "Salik till 20/03/2024" (staff) or the engine's "Salik [& Darb] from
 * 01/09/2026 till 20/09/2026 (...)" -> { from, till } as UTC-midnight ms (null when not written). The month's
 * SALIK PERIOD columns come from them (owner, 4 Oct 2026: a re-pulled calculator showed "- - -" where staff had typed
 * the periods) - monthly_fill.py twin.
 */
const MS_PER_DAY = 86400000;
const MONTH_NAMES = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october',
    'november', 'december'];

// edit distance, small words only (a month spelt with a letter or two off: "Janaury")
const editDistance = (a, b) => {
    const row = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i += 1) {
        let prev = row[0];
        row[0] = i;
        for (let j = 1; j <= b.length; j += 1) {
            const keep = row[j];
            row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
            prev = keep;
        }
    }
    return row[b.length];
};

// a word that names a month: its full name, its first 3 letters, "sept", or its name with at most 2 letters off -
// never "Marina", "Market", "Decision" (review, 4 Oct 2026)
const monthOf = (word) => {
    const w = String(word || '').toLowerCase();
    for (let i = 0; i < 12; i += 1) {
        const name = MONTH_NAMES[i];
        if (w === name.slice(0, 3) || w === name || (i === 8 && w === 'sept')
            || (w.slice(0, 3) === name.slice(0, 3) && editDistance(w, name) <= 2)) return i + 1;
    }
    return 0;
};

// The date written after "till" / "until" / "from" - "20/03/2024", "25/11" (no year), "September 23", "23 Sept",
// "Janaury 21" (a month by its first 3 letters) - as UTC-midnight ms. With no year: the year of `createdMs` (the day
// Ejar's line was written), one year back when that puts the date more than 15 days after the line.
const writtenDay = (text, createdMs) => {
    const t = String(text || '').replace(/^ *:? */, '').slice(0, 40);
    let d = null; let m = null; let y = null;
    // dd/mm[/yy[yy]], or dd.mm.yy[yy] - a dotted one only with its year ("10.5 AED" is no date)
    let x = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{4}|\d{2}))?(?!\d)/.exec(t) || /^(\d{1,2})\.(\d{1,2})\.(\d{4}|\d{2})(?!\d)/.exec(t);
    if (x) { d = +x[1]; m = +x[2]; y = x[3] ? +x[3] : null; } else {
        x = /^([A-Za-z]{3,})\.? *(\d{1,2})(?!\d)(?: *,? *(\d{4})(?!\d))?/.exec(t);
        if (x && monthOf(x[1])) { m = monthOf(x[1]); d = +x[2]; y = x[3] ? +x[3] : null; } else {
            x = /^(\d{1,2}) *([A-Za-z]{3,})(?: *,? *(\d{4})(?!\d))?/.exec(t);
            if (x && monthOf(x[2])) { d = +x[1]; m = monthOf(x[2]); y = x[3] ? +x[3] : null; }
        }
    }
    if (!d || !m || d > 31 || m > 12) return null;
    if (y !== null && y < 100) y += 2000;
    if (y === null) {
        if (createdMs == null) return null;
        y = new Date(createdMs).getUTCFullYear();
        const guess = parseSheetDate(`${d}/${m}/${y}`);
        if (guess && guess.getTime() > createdMs + 15 * MS_PER_DAY) y -= 1;
    }
    const out = parseSheetDate(`${d}/${m}/${y}`);
    return out ? out.getTime() : null;
};

// the engine's monthly line "October Salik (CC 31047_5)" (owner, 5 Oct 2026): its month, up to the day before the line
// was written when that is earlier (the month still running); the year of the line's own day (one back for a December
// line written in January)
const monthLinePeriod = (c, createdMs) => {
    const x = /^ *([A-Za-z]{3,}) +salik\b(?: *& *darb)?(?: +(\d{4})\b)?/i.exec(c);
    const m = x ? monthOf(x[1]) : 0;
    if (!m || createdMs == null) return { from: null, till: null };
    const madeDay = Math.floor((createdMs + 4 * 3600 * 1000) / MS_PER_DAY);     // Ejar's UTC time: its Dubai day
    const made = new Date(madeDay * MS_PER_DAY);
    const y = x[2] ? +x[2] : made.getUTCFullYear() - (m > made.getUTCMonth() + 1 ? 1 : 0);
    // the days the engine's line says it covers: "October Salik 2026 (01/10-10/10 · ..."
    const span = /\((\d{2})\/(\d{2})-(\d{2})\/(\d{2})\b/.exec(c);
    if (span && +span[2] === m && +span[4] === m) {
        const a = parseSheetDate(`${+span[1]}/${m}/${y}`);
        const b = parseSheetDate(`${+span[3]}/${m}/${y}`);
        if (a && b) return { from: a.getTime(), till: b.getTime() };
    }
    const first = parseSheetDate(`1/${m}/${y}`);
    const next = parseSheetDate(`1/${(m % 12) + 1}/${y + (m === 12 ? 1 : 0)}`);
    if (!first || !next) return { from: null, till: null };
    const last = next.getTime() - MS_PER_DAY;
    const before = madeDay * MS_PER_DAY - MS_PER_DAY;
    return { from: first.getTime(), till: before >= first.getTime() && before < last ? before : last };
};

export const salikPeriodOf = (comment, createdMs = null) => {
    const c = String(comment || '').replace(/\s/g, ' ');      // one kind of space (a pasted non-breaking one too)
    const after = (re) => { const k = re.exec(c); return k ? writtenDay(c.slice(k.index + k[0].length), createdMs) : null; };
    // "till" in any of the spellings staff use; with none, a date written right after the word Salik ("Salik 08/04/2026")
    const till = after(/\b(?:till|til|until|untill)\b/i);
    const from = after(/\bfrom\b/i);
    const t = till != null ? till : after(/\bsalik\b/i);
    if (from == null && t == null) return monthLinePeriod(c, createdMs);    // "October Salik (...)": the month
    return { from, till: t };
};

/**
 * Index of the month row a dated item belongs to: the last row whose start date is
 * on or before the item's date. Items before the first row fall into the first row;
 * items after the last row fall into the last row (it soaks up the tail).
 */
const rowIndexForDate = (d, startTs) => {
    if (!d) return -1;
    const t = d.getTime();
    let found = -1;
    for (let i = 0; i < startTs.length; i++) {
        if (startTs[i] != null && startTs[i] <= t) found = i;
    }
    return found === -1 ? 0 : found;
};

// Local copy to avoid importing formatDateDDMMYYYY (keeps this helper's deps minimal).
const formatFromDate = (date) => {
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${date.getUTCFullYear()}`;
};

/**
 * The items Ejar bills when a contract is closed that are no Other Charges line (Ejar's calculation itemTypeId ->
 * name; ejar-api-reference): they go into a returned contract's LAST month, under Other, at Ejar's own amount
 * (netPrice, VAT included) - owner, 14 Oct 2026. Rent (1), fines (5), Tolls (14) and Other Charges (23) are not here:
 * the rent is the calculator's, the others come line by line. The engine twin: monthly_fill.EJAR_CLOSE_ITEMS.
 */
export const EJAR_CLOSE_ITEMS = {
    2: 'Extra Hours', 4: 'Fuel Cost', 9: 'Yelo Shield', 10: 'Yelo Shield Plus', 13: 'Parking Fees',
    16: 'Unlimited Salik', 17: 'Damages',
};

/**
 * Build filled rows from the existing rows plus the real Ejar data.
 *
 * @param {Array}  rows   current calculator rows (each has start/end DD/MM/YYYY)
 * @param {Object} ejar   { vouchers, otherCharges, trafficViolations, calculation } from
 *                        finesService.ejarContract. Fines come from Ejar's Traffic
 *                        Violations (complete per-contract), NOT the RTA-scrape DB.
 * @param {Object} [options]
 * @param {boolean} [options.closed]  the contract is returned: Ejar's close items (EJAR_CLOSE_ITEMS) go into the
 *                        last month's Other
 * @returns {{ rows: Array, summary: Object }}
 */
export const fillRowsFromEjar = (rows, ejar, { closed = false } = {}) => {
    const n = (rows || []).length;
    const summary = {
        payments: 0, salik: 0, parking: 0, other: 0, fines: 0,
        paymentsCount: 0, otherCount: 0, finesCount: 0, closeCount: 0, skipped: 0, months: n,
    };
    if (!n) return { rows: rows || [], summary };

    const startTs = rows.map((r) => {
        const d = parseSheetDate(r.start);
        return d ? d.getTime() : null;
    });

    // Fresh per-month accumulators (a full re-sync overwrites the operational columns).
    const acc = rows.map(() => ({
        paid: 0, lastPaidDate: null,
        salik: 0, salikCount: 0, salikFrom: null, salikTill: null,
        parking: 0, parkingList: [],
        other: 0, otherList: [],
        fine: 0, finesList: [],
    }));

    // --- Payments (Ejar vouchers) -------------------------------------------
    for (const v of (ejar && ejar.vouchers) || []) {
        // the one voucher rule (services/ejarVouchers, owner 1 Oct 2026): Receipt in, Disbursement / Refund out (a
        // Disbursement at Close or Cancel Contract was counted as PAID), inactive / deleted skipped, deposit never rent
        const sign = voucherSign(v);
        if (!sign || isDepositVoucher(v)) continue;
        const d = toDate(v && v.voucherDateTime);
        const idx = rowIndexForDate(d, startTs);
        if (idx < 0) { summary.skipped++; continue; }
        const raw = Number(v && v.amount) || 0;
        const signed = sign * raw;                           // money back to the customer reduces the month's paid
        acc[idx].paid += signed;
        const prev = acc[idx].lastPaidDate;
        if (d && (!prev || d.getTime() > prev.getTime())) acc[idx].lastPaidDate = d;
        summary.payments += signed;
        summary.paymentsCount++;
    }

    // --- Salik / Parking / Other (Ejar other charges) -----------------------
    for (const oc of (ejar && ejar.otherCharges) || []) {
        if (oc && oc.isDeleted) continue;
        const d = toDate(oc && oc.creationTime);
        const idx = rowIndexForDate(d, startTs);
        if (idx < 0) { summary.skipped++; continue; }
        const base = Number(oc && oc.amount && oc.amount.value) || 0;
        const amount = round2(oc && oc.isTaxable ? withVat(base) : base);   // store VAT-inclusive
        const title = String((oc && oc.comment) || '').trim();
        const bucket = classifyOtherCharge(oc && oc.comment, oc && oc.isParking);
        const item = {
            amount, title, note: (oc && oc.plateNumber) || '',
            isVatInclusive: true, source: 'ejar', id: (oc && oc.id) || `${idx}-${amount}-${title}`,
        };
        if (bucket === 'salik') {
            acc[idx].salik += amount;
            acc[idx].salikCount += salikTripsOf(oc && oc.comment);
            const period = salikPeriodOf(oc && oc.comment, d ? d.getTime() : null);
            if (period.till != null && (acc[idx].salikTill == null || period.till > acc[idx].salikTill)) acc[idx].salikTill = period.till;
            if (period.from != null && (acc[idx].salikFrom == null || period.from < acc[idx].salikFrom)) acc[idx].salikFrom = period.from;
            summary.salik += amount;
        } else if (bucket === 'parking') {
            acc[idx].parking += amount;
            acc[idx].parkingList.push(item);
            summary.parking += amount;
        } else {
            acc[idx].other += amount;
            acc[idx].otherList.push(item);
            summary.other += amount;
        }
        summary.otherCount++;
    }

    // --- Fines (Ejar Traffic Violations — complete per-contract) -------------
    for (const f of (ejar && ejar.trafficViolations) || []) {
        const d = toDate(f && (f.trafficDate || f.trafficViolationDate || f.date));
        const idx = rowIndexForDate(d, startTs);
        if (idx < 0) { summary.skipped++; continue; }
        const amount = round2(Number((f && f.amount) || 0));   // Ejar fine amount is final (no VAT)
        acc[idx].fine += amount;
        acc[idx].finesList.push({
            ticketNo: (f && (f.trafficNo || f.trafficViolationNumber || f.ticketNo)) || '',
            amount,
            plate: (f && (f.plateNo || f.plateNumber || f.plate)) || '',
            date: (f && (f.trafficDate || f.trafficViolationDate || f.date)) || '',
            note: (f && f.note) || '',
            source: 'ejar',
        });
        summary.fines += amount;
        summary.finesCount++;
    }

    // --- What Ejar bills at the close (a returned contract only): its last month -------------------
    const items = closed ? ((((ejar && ejar.calculation) || {}).calculationResult || {}).calculationItems) : null;
    if (closed && !Array.isArray(items)) {
        // Ejar's calculation not read this time (it 500s / times out now and then): the close items the last month
        // already holds stay as they are - never taken out and put back on the next good read (review, 14 Oct 2026)
        const had = rows[n - 1] && Array.isArray(rows[n - 1].other_list) ? rows[n - 1].other_list : [];
        had.filter((x) => x && x.source === 'ejar-close').forEach((x) => {
            const amount = round2(Number(x.amount) || 0);
            acc[n - 1].other += amount;
            acc[n - 1].otherList.push(x);
            summary.other += amount;
            summary.closeCount++;
        });
    }
    (Array.isArray(items) ? items : []).forEach((it, k) => {
        const typeId = Number(it && it.itemTypeId);
        if (!EJAR_CLOSE_ITEMS[typeId]) return;
        const amount = round2(Number(it && it.netPrice) || 0);
        if (amount <= 0) return;
        const last = acc[n - 1];
        last.other += amount;
        last.otherList.push({
            amount, title: String((it && it.itemName) || '').trim() || EJAR_CLOSE_ITEMS[typeId], note: 'Ejar at close',
            isVatInclusive: true, source: 'ejar-close', id: `ejar-close-${typeId}-${k}`,
        });
        summary.other += amount;
        summary.closeCount++;
    });

    const filled = rows.map((r, i) => {
        const a = acc[i];
        return {
            ...r,
            salik: round2(a.salik),
            salikHasVat: a.salik > 0 ? true : r.salikHasVat,
            salik_count: a.salikCount,   // trips, 0 when none (the Excel's Salik Count)
            parking: round2(a.parking),
            parking_list: a.parkingList,
            other: round2(a.other),
            other_list: a.otherList,
            fine: round2(a.fine),
            fines_list: a.finesList,
            paid: round2(a.paid),
            date: a.lastPaidDate ? formatFromDate(a.lastPaidDate) : (a.paid ? r.date : '-'),
        };
    });
    // The SALIK PERIOD of each month with a dated Salik line: up to its "till", from its "from" - else the day after the
    // period before (the calculator's own rule when staff type it), the first one from the first month's start. A month
    // with no dated Salik line keeps what was typed (and the next period follows on from it).
    let prevTill = null;
    const newRows = filled.map((row, i) => {
        const a = acc[i];
        if (a.salikTill == null) {
            const own = parseSheetDate(row.salikTo || '');
            if (own) prevTill = own.getTime();
            return row;
        }
        const from = a.salikFrom != null ? a.salikFrom : (prevTill != null ? prevTill + MS_PER_DAY : startTs[0]);
        if (from != null && from > a.salikTill) return row;    // a period that would end before it starts: kept as typed
        prevTill = a.salikTill;
        return { ...row, salikFrom: from != null ? formatFromDate(new Date(from)) : (row.salikFrom || ''), salikTo: formatFromDate(new Date(a.salikTill)) };
    });

    summary.payments = round2(summary.payments);
    summary.salik = round2(summary.salik);
    summary.parking = round2(summary.parking);
    summary.other = round2(summary.other);
    summary.fines = round2(summary.fines);
    return { rows: newRows, summary };
};

export default fillRowsFromEjar;
